import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { useCourseLabels } from "../../center";
import { useTherapist } from "../../../context/useTherapist";
import { CouplePatientAPI, useGroupPatients, groupLabel, listAllTherapistPatientsResponse } from "../../patient";
import type { PatientOption } from "../../../shared/ui/patient/PatientFinder";
import { isSubjectOffered, useSessionPreferences } from "../../center";

export const SUBJECT_PATIENTS_KEY = "scheduler-patients";
export const SUBJECT_COUPLES_KEY = "scheduler-couples";

/**
 * Everyone a therapist can book with: their patients, couples and groups, in
 * one list.
 *
 * The two come from different endpoints and load independently, so a failure
 * in either is reported rather than silently offering half the caseload as if
 * it were all of it.
 */
export function useSchedulerSubjects(enabled = true) {
    const { therapist } = useTherapist();
    // A training centre's student, with the course they are on under the name.
    const { courseOf } = useCourseLabels();

    const patientsQuery = useQuery({
        queryKey: [SUBJECT_PATIENTS_KEY, therapist?.id],
        queryFn: async () => {
            const { data } = await listAllTherapistPatientsResponse(therapist?.id as string);
            return data.patients;
        },
        enabled: enabled && Boolean(therapist?.id),
    });

    const couplesQuery = useQuery({
        queryKey: [SUBJECT_COUPLES_KEY, therapist?.id],
        queryFn: async () => {
            const { data } = await CouplePatientAPI.listByTherapist(
                therapist?.id as string
            );
            return data;
        },
        enabled: enabled && Boolean(therapist?.id),
    });

    const groupsQuery = useGroupPatients(enabled && Boolean(therapist?.id));

    // Individuals first, then couples, then groups: a therapist looking for one
    // person should not have to scroll past the pairs to reach them.
    const { modes } = useSessionPreferences();

    const allOptions = useMemo<PatientOption[]>(
        () => [
            ...(patientsQuery.data ?? []).map((patient) => ({
                id: patient.id,
                name: `${patient.first_name} ${patient.last_name}`,
                kind: "patient" as const,
                detail: courseOf(patient.course_id),
                courseId: patient.course_id ?? null,
            })),
            ...(couplesQuery.data ?? []).map((couple) => ({
                id: couple.id,
                name: couple.display_name,
                kind: "couple" as const,
            })),
            ...groupsQuery.groups.map((group) => ({
                id: group.id,
                name: groupLabel(group),
                kind: "group" as const,
            })),
        ],
        [patientsQuery.data, couplesQuery.data, groupsQuery.groups, courseOf]
    );

    // Keyed by subject id, so a couple booking — which carries no patient_id —
    // still resolves to a name instead of rendering blank.
    // Only the kinds of session the centre runs are offered to book. Every
    // name is still known, so a booking made before a kind was switched off
    // keeps reading as somebody.
    const options = useMemo(
        () => allOptions.filter((option) => isSubjectOffered(modes, option.kind)),
        [allOptions, modes]
    );

    const names = useMemo(() => {
        const lookup = new Map<string, string>();
        allOptions.forEach((option) => lookup.set(option.id, option.name));
        return lookup;
    }, [allOptions]);

    return {
        options,
        names,
        isLoading: patientsQuery.isLoading || couplesQuery.isLoading || groupsQuery.loading,
        isError: patientsQuery.isError || couplesQuery.isError || groupsQuery.failed,
    };
}
